export const blogPosts = [
  {
    id: 'non-linear-career-myth',
    slug: 'non-linear-career-myth',
    title: 'The Myth of the Linear Career Path',
    excerpt: 'Why the traditional career ladder is outdated and how embracing a non-linear path can lead to more fulfilling work in STEM.',
    content: `The traditional career path—climb the ladder, stay in your lane, reach the top—is a myth that's holding back countless talented professionals. In today's rapidly evolving STEM landscape, the most successful careers look more like a jungle gym than a ladder.

## Why Linear Thinking Fails

The industries we work in are changing faster than ever. A skill that's in high demand today might be automated tomorrow. The rigid career ladder doesn't account for:

- Industry disruptions and pivots
- Personal growth and changing interests
- Life circumstances and priorities
- New opportunities that didn't exist before

## Embracing the Non-Linear Path

A non-linear career path isn't about being unfocused—it's about being adaptable. It's about connecting dots that others can't see and building a unique combination of skills and experiences.

### Key Strategies:

1. **Stack Your Skills** - Combine technical expertise with unexpected complementary abilities
2. **Follow Your Curiosity** - Allow yourself to explore adjacent fields
3. **Build Bridges** - Create connections between different domains you've worked in
4. **Stay Open** - The best opportunities often come from unexpected places

## Your Trajectory, Your Terms

Remember: your career is not a destination but a journey. The most interesting paths are the ones we design ourselves, not the ones handed to us by tradition.

Ready to map your own trajectory? Start by asking yourself: What unique combination of experiences do I bring? What problems am I uniquely positioned to solve?`,
    author: 'Diana',
    date: '2025-01-10',
    readTime: '5 min read',
    category: 'Career',
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800',
    featured: true
  },
  {
    id: 'burnout-recovery-stem',
    slug: 'burnout-recovery-stem',
    title: '5 Signs of STEM Burnout (And What to Do About It)',
    excerpt: 'Burnout in technical fields often looks different. Learn to recognize the signs and discover actionable recovery strategies.',
    content: `Burnout in STEM careers often creeps up silently. We're trained to solve problems, push through challenges, and optimize everything—including ourselves. But this mindset can blind us to the warning signs until we're completely depleted.

## The 5 Warning Signs

### 1. Cynicism About Your Work
Remember when you were excited about solving problems? If every new project feels like a burden and you've lost that spark of curiosity, that's a red flag.

### 2. Decreased Productivity Despite Longer Hours
You're working more but accomplishing less. Tasks that used to take an hour now take all day. Your brain feels foggy.

### 3. Physical Symptoms
Headaches, insomnia, constant fatigue, frequent illness—your body is trying to tell you something.

### 4. Detachment from Colleagues
You're isolating yourself, skipping team events, and dreading meetings. Human connection feels exhausting.

### 5. Imposter Syndrome Intensifying
Burnout often amplifies self-doubt. You might feel like you've lost your edge or that everyone will soon discover you're not as competent as they thought.

## Recovery Strategies

1. **Set Non-Negotiable Boundaries** - Define work hours and stick to them
2. **Prioritize Sleep** - Yes, even if that deadline is looming
3. **Reconnect with Your 'Why'** - Remember what drew you to this field
4. **Seek Support** - Talk to a mentor, therapist, or trusted colleague
5. **Consider a Reset** - Sometimes a strategic break or change is necessary

## Prevention is Possible

Building sustainable work habits isn't a luxury—it's essential for a long, fulfilling career in STEM. Your well-being is not separate from your success; it's the foundation of it.`,
    author: 'Diana',
    date: '2025-01-05',
    readTime: '7 min read',
    category: 'Wellness',
    image: 'https://images.unsplash.com/photo-1493836512294-502baa1986e2?auto=format&fit=crop&w=800',
    featured: true
  },
  {
    id: 'stem-parent-guide',
    slug: 'stem-parent-guide',
    title: 'Thriving as a STEM Parent: A Realistic Guide',
    excerpt: 'Balancing a demanding technical career with parenthood is challenging but possible. Here\'s what actually works.',
    content: `Let's be honest: balancing a demanding STEM career with parenthood is hard. Really hard. But it's not impossible, and you don't have to sacrifice one for the other.

## The Reality Check

First, let go of the myth of "having it all." You can have a fulfilling career AND be a present parent, but probably not at 100% capacity in both areas simultaneously. And that's okay.

## What Actually Works

### Ruthless Prioritization
Not everything is equally important. Learn to identify what truly matters—both at work and at home—and let go of the rest.

### Building Your Village
You cannot do this alone. Whether it's family, friends, or paid help, building a support system is essential, not optional.

### Flexible Work Arrangements
Remote work, flexible hours, compressed workweeks—explore what options are available to you and advocate for what you need.

### Quality Over Quantity
A fully present hour with your kids beats a distracted evening. When you're with family, be WITH family.

### Self-Compassion
You will miss things. You will feel guilty. Learn to forgive yourself and move forward.

## Career Strategies for Parents

1. **Be Strategic About Projects** - Choose work that aligns with your current life stage
2. **Communicate Proactively** - Set expectations with your team about your availability
3. **Invest in Efficiency** - Automate, delegate, and streamline wherever possible
4. **Maintain Visibility** - Find ways to demonstrate your impact even with reduced hours

## Remember This

Your career is a marathon, not a sprint. The years when your children are young are a season. Your career will evolve, and so will your capacity. Be patient with yourself.`,
    author: 'Diana',
    date: '2024-12-28',
    readTime: '6 min read',
    category: 'Work-Life Balance',
    image: 'https://images.pexels.com/photos/4260325/pexels-photo-4260325.jpeg?auto=compress&cs=tinysrgb&w=800',
    featured: false
  }
];

export const getPostBySlug = (slug) => {
  return blogPosts.find(post => post.slug === slug);
};

export const getFeaturedPosts = () => {
  return blogPosts.filter(post => post.featured);
};

export const getRecentPosts = (count = 3) => {
  return [...blogPosts]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, count);
};
